import React, { useState, useEffect } from 'react';
import "../../templates/Templates.css";
import "../../templates/tables/Table.css";
import "../ComponentsGlobalStyle.css";
import HistoricalDataRow from './HistoricalDataRow';
import { formatAccordingToType } from '../../templates/Types';  
import * as con from '../../GlobalConstants';  

import chevron from '../../img/icons/chevron.png';
import doubleChevron from '../../img/icons/double-chevron.png';

const calculateCellStyles = (width) => {
    if (width <= 768) {
        return {
            fontSize: '10px',
            padding: '3px',
            minWidth: '5px',
        };
    } else if (width <= 1480) {
        return {
            fontSize: '18px',
            padding: '1px',
            minWidth: '100px',
        };
    } else {
        return {
            fontSize: '25px',
            padding: '10px',
            minWidth: '130px',
        };
    }
};

const calculateScreenPercentage = (width) => {
    if (width <= 768) {
        return 0.22; 
    } else if (width <= 1480) {
        return 0.3; 
    } else {
        return 0.4; 
    }
};

const calculateRecordsPerPage = (height, cellStyles, screenPercentage) => {
    const rowHeight = parseInt(cellStyles.fontSize) + parseInt(cellStyles.padding) * 2;
    return Math.floor((height * screenPercentage) / rowHeight);
};

function HistoricalDataTable({ fixedSize, data }) {
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [cellStyles, setCellStyles] = useState(calculateCellStyles(window.innerWidth));
    
    // eslint-disable-next-line no-unused-vars
    const [screenPercentage, setScreenPercentage] = useState(calculateScreenPercentage(window.innerWidth));

    useEffect(() => {
        const adjustTableStyles = () => {
            const newWidth = window.innerWidth;
            const newCellStyles = calculateCellStyles(newWidth);
            const newScreenPercentage = calculateScreenPercentage(newWidth);

            setCellStyles(newCellStyles);
            setScreenPercentage(newScreenPercentage);
            setRecordsPerPage(calculateRecordsPerPage(window.innerHeight, newCellStyles, newScreenPercentage));
        };

        adjustTableStyles(); // Set initial values
        window.addEventListener('resize', adjustTableStyles);
        return () => window.removeEventListener('resize', adjustTableStyles);
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [data]);

    if (!data || data.length === 0) {
        return <div>No hay datos disponibles</div>;
    }

    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = data.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(data.length / recordsPerPage);

    const handleFirstPage = () => setCurrentPage(1);
    const handlePreviousPage = () => setCurrentPage(prevPage => Math.max(prevPage - 1, 1));
    const handleNextPage = () => setCurrentPage(prevPage => Math.min(prevPage + 1, totalPages));
    const handleLastPage = () => setCurrentPage(totalPages);

    return (
        <div>
            <div 
                className="pagination" 
                style={{ 
                    textAlign: 'center', 
                    marginBottom: '14px',
                    fontSize: cellStyles.fontSize,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}
            >
                <button 
                    onClick={handleFirstPage} 
                    disabled={currentPage === 1} 
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 'inherit',
                    }}
                >
                    <img src={doubleChevron} alt="Primera página" style={{ width: '12px', height: '12px', transform: 'scaleX(-1)', filter: 'invert(100%)' }} />
                </button>
                <button 
                    onClick={handlePreviousPage} 
                    disabled={currentPage === 1} 
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 'inherit',
                    }}
                >
                    <img src={chevron} alt="Página anterior" style={{ width: '12px', height: '12px', transform: 'scaleX(-1)', filter: 'invert(100%)' }} />
                </button>
                <span style={{ margin: '0 10px', color: 'white', lineHeight: '16px', marginTop: '-4px' }}>
                    Página {currentPage} de {totalPages}
                </span>
                <button 
                    onClick={handleNextPage} 
                    disabled={currentPage === totalPages} 
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 'inherit',
                    }}
                >
                    <img src={chevron} alt="Siguiente página" style={{ width: '12px', height: '12px', filter: 'invert(100%)' }} />
                </button>
                <button 
                    onClick={handleLastPage} 
                    disabled={currentPage === totalPages} 
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 'inherit',
                    }}
                >
                    <img src={doubleChevron} alt="Última página" style={{ width: '12px', height: '12px', filter: 'invert(100%)' }} />
                </button>
            </div>

            <div className="contenedor-tabla" style={{ fontSize: cellStyles.fontSize, tableLayout: 'fixed' }}>
                <div className="flex-tabla">
                    <div className="flex-fila header-row">
                        <div className="flex-celda" style={{ padding: cellStyles.padding, textAlign: 'center', minWidth: cellStyles.minWidth }}>FECHA</div>
                        <div className="flex-celda" style={{ padding: cellStyles.padding, textAlign: 'center', minWidth: cellStyles.minWidth }}>MONTO</div>
                        <div className="flex-celda" style={{ padding: cellStyles.padding, textAlign: 'center', minWidth: cellStyles.minWidth }}>TRM</div>
                        <div className="flex-celda" style={{ padding: cellStyles.padding, textAlign: 'center', minWidth: cellStyles.minWidth }}>APERTURA</div>
                        <div className="flex-celda" style={{ padding: cellStyles.padding, textAlign: 'center', minWidth: cellStyles.minWidth }}>MÁX</div>
                        <div className="flex-celda" style={{ padding: cellStyles.padding, textAlign: 'center', minWidth: cellStyles.minWidth }}>MÍN</div>
                        <div className="flex-celda" style={{ padding: cellStyles.padding, textAlign: 'center', minWidth: cellStyles.minWidth }}>CIERRE</div>
                    </div>
                </div>

                <div className="flex-tabla">
                    {currentRecords.map((data, index) => (
                        <HistoricalDataRow 
                            key={index} 
                            rowId={index} 
                            data={{
                                ...data,
                                trm: formatAccordingToType(con.MONEY, data.trm, { allowDecimals: true }), 
                                apertura: formatAccordingToType(con.MONEY, data.apertura, { allowDecimals: true }), 
                                max: formatAccordingToType(con.MONEY, data.max, { allowDecimals: true }), 
                                min: formatAccordingToType(con.MONEY, data.min, { allowDecimals: true }), 
                                cierre: formatAccordingToType(con.MONEY, data.cierre, { allowDecimals: true })
                            }} 
                            cellStyles={cellStyles}  
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

export default HistoricalDataTable;
